import { clamp } from 'lodash';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import * as FlatModels from '@medsurf/flat-models';
import { MenuSharedFacade, SlideViewerFacade } from '@medsurf/flat-facades';

let startY = 0;

@Component({
    selector: 'medsurf-sequence-control',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './sequenceControl.component.html',
    styleUrls: ['./sequenceControl.component.scss'],
    standalone: false
})
export class SequenceControlComponent implements AfterViewInit {
  /**
   * View Children
   */
  @ViewChild('sequenceList', {static: false}) public sequenceListElement: ElementRef<HTMLElement>;

  /**
   * Members
   */
  public sequenceListHeight = 200;

  /**
   * Constructor
   *
   * @param slideViewerFacade
   * @param menuSharedFacade
   */
  public constructor(public slideViewerFacade: SlideViewerFacade,
                     public menuSharedFacade: MenuSharedFacade) {
  }

  /**
   * Ng After View Init
   */
  ngAfterViewInit(): void {
    this.handleResize();
  }

  /**
   * Tracks items by their id
   * @param i
   * @param item
   */
  public trackBy(i, item) {
    return item.id;
  }

  /**
   * Get Sequence Control
   *
   * @param sequenceItems
   */
  public getSequenceControl(sequenceItems: FlatModels.SequenceControlViewerModels.SequenceItem[]): FlatModels.SequenceControlViewerModels.SequenceControl {
    if (!sequenceItems) {
      return null;
    }
    return {
      up: {
        active: sequenceItems.some(item => item.hidden && item.angle > 0), // imageIndex !== 0,
        color: sequenceItems.reduce((c, item) => item.hidden && item.angle > 0 && item.color ? item.color : c, '')
      },
      down: {
        active: sequenceItems.some(item => item.hidden && item.angle < 0), // imageIndex !== this.sequenceItems.length - 1,
        color: sequenceItems.reduceRight((c, item) => item.hidden && item.angle < 0 && item.color ? item.color : c, '')
      }
    }
  }

  /**
   * Get Sequence Items
   *
   * @param images
   * @param medias
   * @param themeColor
   * @param imageIndex
   */
  public getSequenceItems(images: FlatModels.ImageEntityModels.Image[],
                          medias: FlatModels.MediaEntityModels.MediaEntityType[],
                          themeColor: string,
                          imageIndex: number): FlatModels.SequenceControlViewerModels.SequenceItem[] {
    if (!images || !Number.isInteger(imageIndex)) {
      return [];
    }
    const hasDepth = medias
      .filter((media) => media.type === 'MediaDeepzoom' || media.type === 'MediaImage')
      .every((media) => !!media.dimensions?.depth?.trim());

    return images.map((image, index) => {
      const media = medias.find(m => m.id === image.media as unknown as string);
      const active = imageIndex === index;
      const angleStep = 200 / this.sequenceListHeight * 6;
      const angle = clamp((imageIndex - index) * angleStep, -60, 60);
      const depth = 'dimensions' in media ? media?.dimensions?.depth : null;
      let title;
      if (hasDepth) {
        const pads = String(images.length).length + 1;
        title = `${String(depth).padStart(pads, ' ')}`;
      } else {
        const pads = String(images.length).length;
        title = `${String(index + 1).padStart(pads, ' ')}/${images.length}`;
      }
      const annotated = image.annotations?.length > 0 || image.pois?.length > 0;
      const color = annotated ? themeColor : '';
      const hidden = Math.abs(angle) === 60;

      return {
        id: image.id,
        sequenceNumber: image.sequenceNumber,
        active,
        angle,
        title,
        color,
        hidden
      };
    })
  }

  /**
   * Get Current Title
   *
   * @param sequenceItems
   */
  public getCurrentTitle(sequenceItems: FlatModels.SequenceControlViewerModels.SequenceItem[]) {
    const item = sequenceItems.find(i => i.active);
    return item?.title || '';
  }

  /**
   * On wheel
   *
   * @param event
   * @param imageIndex
   */
  public onWheel(event: WheelEvent,
                 imageIndex: number) {
    const delta = Math.sign(event.deltaY);
    this.slideViewerFacade.requestSetImageIndex(imageIndex + delta);
  }

  /**
   * On Touch Start
   *
   * @param event
   */
  public onTouchStart(event: TouchEvent) {
    if (event.targetTouches.length === 1) {
      startY = event.targetTouches[0].clientY;
    } 
  }

  /**
   * Handle Touch Move
   *
   * @param event
   * @param imageIndex
   */
  handleTouchMove(event: TouchEvent,
                  imageIndex: number) {
    if (startY) {
      const delta = startY - event.targetTouches[0].clientY;
      if (Math.abs(delta) > 25) {
        startY = event.targetTouches[0].clientY;
        this.slideViewerFacade.requestSetImageIndex(imageIndex + Math.sign(delta));
      }
    }
  }

  /**
   * Handle Touch End
   */
  @HostListener('window:touchend')
  handleTouchEnd() {
    if (startY) {
      startY = 0;
    }
  }

  /**
   * Handle Resize
   */
  @HostListener('window:resize')
  handleResize() {
    if (
      this.sequenceListElement?.nativeElement &&
      this.sequenceListElement?.nativeElement?.clientHeight !== this.sequenceListHeight
    ) {
      this.sequenceListHeight = this.sequenceListElement?.nativeElement?.clientHeight;
    }
  }
}
